import { useState } from "react";
import { Project, projectsApi } from "../../lib/api/projects";

interface ProjectSettingsProps {
  project: Project;
  onClose: () => void;
  onProjectUpdated: (updatedProject: Project) => void;
}

export function ProjectSettings({
  project,
  onClose,
  onProjectUpdated,
}: ProjectSettingsProps) {
  const [formData, setFormData] = useState({
    name: project.name,
    key: project.key,
    description: project.description || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const updatedProject = await projectsApi.update(project._id, {
        name: formData.name,
        description: formData.description,
      });
      onProjectUpdated(updatedProject);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update project");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveProject = async () => {
    if (!window.confirm("Are you sure you want to archive this project?")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedProject = await projectsApi.update(project._id, {
        status: "ARCHIVED",
      });
      onProjectUpdated(updatedProject);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to archive project",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Project Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded mb-6">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Project Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-4 py-2 border rounded focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="key"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Project Key
            </label>
            <input
              type="text"
              id="key"
              value={formData.key}
              disabled
              className="w-full px-4 py-2 border rounded bg-gray-50 text-gray-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              Project key cannot be changed after creation
            </p>
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={4}
              className="w-full px-4 py-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleArchiveProject}
              disabled={loading || project.status === "ARCHIVED"}
              className="text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              {project.status === "ARCHIVED"
                ? "Project Archived"
                : "Archive Project"}
            </button>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
